import { useEffect, useRef, useState } from "react";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DEFAULT_TEMPLATE, PLACEHOLDERS, resolveTemplate } from "../lib/whatsapp";
import { Save, RotateCcw, MessageCircle, Check, FileText, Upload, Trash2, ExternalLink, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_LEAD = {
  lead_no: "L-00042",
  building_name: "Green Meadows",
  city: "Hyderabad",
  locality: "Kondapur",
};
const SAMPLE_CONTACT = { name: "Ramesh Kumar", role: "owner" };

export default function Settings() {
  const { user, setUser } = useAuth();
  const [template, setTemplate] = useState(user?.whatsapp_template || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const effective = template.trim() || DEFAULT_TEMPLATE;
  const preview = resolveTemplate(effective, { lead: SAMPLE_LEAD, contact: SAMPLE_CONTACT, user });

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const r = await api.patch(`/users/${user.id}`, { whatsapp_template: template });
      setUser(r.data);
      localStorage.setItem("sohom_user", JSON.stringify(r.data));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (ex) {
      setError(ex?.response?.data?.detail || ex.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    setTemplate(DEFAULT_TEMPLATE);
    setSaved(false);
  };

  const insertPlaceholder = (ph) => {
    setTemplate((t) => (t.trim() ? `${t}${t.endsWith(" ") ? "" : " "}${ph}` : ph));
  };

  return (
    <Shell>
      <div className="px-4 sm:px-8 py-5 sm:py-7 max-w-3xl">
        <div className="mb-5">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Personal preferences — changes only affect your account ({user?.email}).</p>
        </div>

        <div className="sohom-card p-5 sm:p-6" data-testid="whatsapp-template-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>WhatsApp intro message</h2>
              <p className="text-xs text-slate-500 mt-0.5">This is the default message pre-filled when you tap Share on a lead. You can still tweak it per-lead before sending.</p>
            </div>
          </div>

          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Your template</label>
          <textarea
            className="sohom-input mt-1"
            rows={7}
            placeholder={DEFAULT_TEMPLATE}
            value={template}
            onChange={(e) => { setTemplate(e.target.value); setSaved(false); }}
            data-testid="settings-whatsapp-template"
          />
          <div className="text-[11px] text-slate-400 mt-1">
            {template.length} characters · leave empty to fall back to the default message shown as the placeholder.
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Insert placeholder</div>
            <div className="flex flex-wrap gap-1.5" data-testid="settings-placeholders">
              {PLACEHOLDERS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertPlaceholder(p.key)}
                  className="px-2 py-1 text-[11px] rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700"
                  title={p.hint}
                  data-testid={`settings-placeholder-${p.key.replace(/[{}]/g, "")}`}
                >
                  <span className="font-mono">{p.key}</span> <span className="text-slate-400 ml-1">{p.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">Preview</div>
            <div className="text-xs text-slate-400 mb-2">Rendered against the sample lead <span className="font-mono">{SAMPLE_LEAD.lead_no}</span> · Contact <em>{SAMPLE_CONTACT.name}</em></div>
            <div className="text-sm text-slate-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 whitespace-pre-wrap" data-testid="settings-whatsapp-preview">
              {preview}
            </div>
          </div>

          {error && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="settings-whatsapp-error">
              {error}
            </div>
          )}

          <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
            <button onClick={resetToDefault} className="sohom-btn-secondary" data-testid="settings-whatsapp-reset">
              <RotateCcw className="w-4 h-4" /> Reset to default
            </button>
            <div className="flex items-center gap-3">
              {saved && (
                <span className="text-xs text-emerald-700 font-medium inline-flex items-center gap-1" data-testid="settings-whatsapp-saved">
                  <Check className="w-3.5 h-3.5" /> Saved
                </span>
              )}
              <button onClick={save} disabled={saving} className="sohom-btn-primary" data-testid="settings-whatsapp-save">
                <Save className="w-4 h-4" />
                {saving ? "Saving…" : "Save template"}
              </button>
            </div>
          </div>
        </div>

        {user?.role === "admin" && <BrochureCard />}
      </div>
    </Shell>
  );
}

function BrochureCard() {
  const [meta, setMeta] = useState(null); // {exists, id, url_path, ...}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/brochure");
      setMeta(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load brochure state");
    }
  };
  useEffect(() => { load(); }, []);

  const publicUrl = meta?.exists ? `${API_URL}${meta.url_path}` : "";

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.pdf$/i.test(f.name) && f.type !== "application/pdf") {
      setError("Please pick a PDF file.");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setError("PDF is larger than 20 MB.");
      return;
    }
    setBusy(true); setError(""); setNotice("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/brochure/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNotice("Brochure uploaded — it will be auto-appended to every WhatsApp intro.");
      await load();
      setTimeout(() => setNotice(""), 3500);
    } catch (ex) {
      setError(ex?.response?.data?.detail || ex.message || "Upload failed");
    } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!confirm("Remove the current brochure? The link inside WhatsApp intros will stop appending.")) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await api.delete("/brochure");
      setNotice("Brochure removed.");
      await load();
      setTimeout(() => setNotice(""), 3500);
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const humanSize = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="sohom-card p-5 sm:p-6 mt-5" data-testid="brochure-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-sky-50 flex items-center justify-center text-sky-600 shrink-0">
          <FileText className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Company Brochure</h2>
          <p className="text-xs text-slate-500 mt-0.5">One shared PDF (max 20 MB). Once set, its link is auto-appended to every WhatsApp intro sent from this app.</p>
        </div>
      </div>

      {meta?.exists ? (
        <div className="rounded-md border border-slate-200 p-3 bg-slate-50" data-testid="brochure-current">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-2">
                <FileText className="w-4 h-4 text-sky-600 shrink-0" />
                <span className="truncate" data-testid="brochure-filename">{meta.original_filename}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {humanSize(meta.size)} · uploaded by {meta.uploaded_by_name || "—"}
              </div>
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-sky-600 hover:text-sky-800 underline inline-flex items-center gap-1 mt-1 break-all"
                data-testid="brochure-public-url"
              >
                {publicUrl} <ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onPick} disabled={busy} className="sohom-btn-secondary" data-testid="brochure-replace-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Replace
              </button>
              <button onClick={onDelete} disabled={busy} className="sohom-btn-secondary text-red-600 border-red-200 hover:bg-red-50" data-testid="brochure-delete-btn">
                <Trash2 className="w-4 h-4" /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-slate-300 p-6 text-center bg-slate-50" data-testid="brochure-empty">
          <FileText className="w-8 h-8 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-600 mb-3">No brochure uploaded yet.</p>
          <button onClick={onPick} disabled={busy} className="sohom-btn-primary" data-testid="brochure-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "Uploading…" : "Upload PDF"}
          </button>
        </div>
      )}

      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid="brochure-file-input" />

      {notice && <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="brochure-notice">{notice}</div>}
      {error && <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="brochure-error">{error}</div>}
    </div>
  );
}
